import React, { useEffect, useState } from "react";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  notification,
  Button,
  Row,
  Col,
  Card,
  Tag,
  Select,
  Spin,
  Table,
  DatePicker,
  Empty,
} from "antd";
import { ReloadOutlined, ManOutlined } from "@ant-design/icons";
import ChartCard from "../../charts/ChartCard";
import getNovedades from "../../../services/novedades/getNovedades";
import ExportNovedades from "../../Novedades/NovedadesExcel";
import { useSelector } from "react-redux";

const { RangePicker } = DatePicker;

Chart.register(...registerables, ChartDataLabels);
const { Option } = Select;

const cardStyle = {
  textAlign: "center",
  borderRadius: 20,
  backgroundColor: "#ffffff",
  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
  padding: 20,
  height: "90%",
  paddingTop: "30px",
  marginTop: "20px",
};

const iconContainerStyle = {
  width: 80,
  height: 80,
  borderRadius: "50%",
  backgroundColor: "#F44336",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  margin: "0 auto 10px",
};

const iconStyle = {
  fontSize: 40,
  color: "white",
};

const total = {
  fontSize: 30,
  color: "#333",
  margin: 0,
};

const InformeNovedades = () => {
  const [data, setData] = useState(null);
  const [dataOriginal, setDataOriginal] = useState(null);
  const [dataFilter, setDataFilter] = useState(null);

  const [departamentoSeleccionado, setDepartamentoSeleccionado] =
    useState(null);
  const [municipioSeleccionado, setMunicipioSeleccionado] = useState(null);
  const [tipoNovedadSeleccionado, setTipoNovedadSeleccionado] = useState(null);
  const [numeroVisitaSeleccionado, setNumeroVisitaSeleccionado] =
    useState(null);
  const [rangoFechas, setRangoFechas] = useState(null);

  const [visitasPorDepartamento, setVisitasPorDepartamento] = useState({});
  const [visitasPorMunicipio, setVisitasPorMunicipio] = useState({});
  const [novedadesPorTipoNovedad, setNovedadesPorTipoNovedad] = useState({});
  const [novedadesPorEstadoParticipante, setNovedadesPorEstadoParticipante] =
    useState({});
  const [novedadesPorResponsableEntidad, setNovedadesPorResponsableEntidad] =
    useState(null);

  const [listDepartamento, setListDepartamento] = useState(null);
  const [listMunicipio, setListMunicipio] = useState(null);
  const [listTipoNovedad, setListTipoNovedad] = useState(null);
  const [listMunicipiosPorDepartamento, setListMunicipiosPorDepartamento] =
    useState(null);

  const [indicadores, setIndicadores] = useState({
    totalNovedades: 0,
  });

  const usernameFromState = useSelector((state) => state.auth.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchData = async () => {
    try {
      const response = await getNovedades(usernameFromState.email);
      const { status, data } = response;

      if (status) {
        setData(data);
        setDataOriginal(data); // Guarda la data original
        setDataFilter(data);
        const departamentos = [...new Set(data.map((p) => p.departamento_p))];
        setListDepartamento(departamentos);

        const tipoNovedades = [...new Set(data.map((p) => p.novedad))];
        setListTipoNovedad(tipoNovedades);

        // Agrupar municipios por departamento
        const municipiosPorDepartamento = data.reduce((acc, p) => {
          if (!acc[p.departamento_p]) {
            acc[p.departamento_p] = new Set();
          }
          acc[p.departamento_p].add(p.municipio_p);
          return acc;
        }, {});
        //Almacenamos la lista de municipios por departamento
        setListMunicipiosPorDepartamento(municipiosPorDepartamento);

        calcularIndicadores(data);
      } else {
        notification.error({ message: response.message });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching data.",
      });
    }
  };

  useEffect(() => {
    if (!data) return;

    let visitasFiltradas = [...data];

    if (departamentoSeleccionado) {
      visitasFiltradas = visitasFiltradas.filter(
        (p) => p.departamento_p === departamentoSeleccionado
      );

      // Lista de municipios filtrados por el departamento seleccionado
      const municipios = departamentoSeleccionado
        ? [...listMunicipiosPorDepartamento[departamentoSeleccionado]]
        : [];
      setListMunicipio(municipios);
    }

    if (municipioSeleccionado) {
      visitasFiltradas = visitasFiltradas.filter(
        (p) => p.municipio_p === municipioSeleccionado
      );
    }

    if (tipoNovedadSeleccionado) {
      visitasFiltradas = visitasFiltradas.filter(
        (p) => p.novedad === tipoNovedadSeleccionado
      );
    }
    if (numeroVisitaSeleccionado) {
      visitasFiltradas = visitasFiltradas.filter(
        (p) => p.numero_visita === numeroVisitaSeleccionado
      );
    }
    if (rangoFechas) {
      const [inicio, fin] = rangoFechas;
      visitasFiltradas = visitasFiltradas.filter((visita) => {
        const fechaVisita = new Date(visita.fecha_registro);
        return fechaVisita >= new Date(inicio) && fechaVisita <= new Date(fin);
      });
    }
    calcularIndicadores(visitasFiltradas);
  }, [
    departamentoSeleccionado,
    municipioSeleccionado,
    tipoNovedadSeleccionado,
    numeroVisitaSeleccionado,
    rangoFechas,
    data,
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const calcularIndicadores = (datos) => {
    //Total de novedades
    const totalNovedades = datos.length;
    setIndicadores({ totalNovedades });

    //Novedades por departamento
    const departamentosTemp = {};
    datos.forEach((p) => {
      departamentosTemp[p.departamento] =
        (departamentosTemp[p.departamento] || 0) + 1;
    });
    setVisitasPorDepartamento(departamentosTemp);

    //Novedades por municipio
    const municipiosTemp = {};
    datos.forEach((p) => {
      municipiosTemp[p.municipio] = (municipiosTemp[p.municipio] || 0) + 1;
    });
    setVisitasPorMunicipio(municipiosTemp);

    //Novedades por tipo de novedad
    const tipoNovedadTemp = {};
    datos.forEach((p) => {
      tipoNovedadTemp[p.alias_novedad] =
        (tipoNovedadTemp[p.alias_novedad] || 0) + 1;
    });
    setNovedadesPorTipoNovedad(tipoNovedadTemp);

    //Novedades por responsable entidad
    const responsableEntidadTemp = {};
    datos.forEach((p) => {
      responsableEntidadTemp[p.responsable] =
        (responsableEntidadTemp[p.responsable] || 0) + 1;
    });
    setNovedadesPorResponsableEntidad(responsableEntidadTemp);

    //Novedades porestado de participante
    const estadoParticipanteTemp = {};
    datos.forEach((p) => {
      estadoParticipanteTemp[p.estado] =
        (estadoParticipanteTemp[p.estado] || 0) + 1;
    });
    setNovedadesPorEstadoParticipante(estadoParticipanteTemp);

    setDataFilter(datos);
  };

  const columns = [
    {
      title: "Fecha",
      dataIndex: "fecha_registro",
      key: "fecha_registro",
      width: 150,
    },
    {
      title: "Tipo novedad",
      dataIndex: "novedad",
      key: "novedad",
      width: 150,
    },
    {
      title: "Codigo SIRR",
      dataIndex: "codigoSIRR",
      key: "codigoSIRR",
      width: 150,
    },
    {
      title: "Codigo PRE",
      dataIndex: "codigoPREPP",
      key: "codigoPREPP",
      width: 150,
    },
    {
      title: "Número identificación",
      dataIndex: "numeroIdentificacion",
      key: "numeroIdentificacion",
      width: 150,
    },

    {
      title: "Nombre",
      dataIndex: "nombreParticipante",
      key: "nombreParticipante",
      width: 150,
    },
    {
      title: "Departamento",
      dataIndex: "departamento",
      key: "departamento",
      width: 150,
    },
    {
      title: "Municipio",
      dataIndex: "municipio",
      key: "municipio",
      width: 150,
    },
  ];

  if (!data) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
        <p style={{ marginTop: 16, fontSize: 16, color: "#888" }}>
          Cargando datos...
        </p>
      </div>
    );
  }

  return (
    <>
      {dataOriginal.length === 0 ? (
        <Empty
          description="No hay novedades"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Card style={{ padding: 20 }}>
          <Tag
            color="#D0E0F2"
            style={{
              color: "#145BAD",
              fontSize: "25px",
              fontWeight: "bold",
              padding: "8px 15px",
              borderRadius: "8px",
              textAlign: "center",
              display: "block",
              marginBottom: "15px",
            }}
          >
            INFORME DE NOVEDADES
          </Tag>
          {/* Filtros */}
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            {/* Filtros - Departamento*/}
            <Col xs={24} sm={8} md={5} lg={5}>
              <Select
                placeholder="Selecciona un Departamento"
                style={{ width: "100%" }}
                value={departamentoSeleccionado}
                onChange={(value) => {
                  setDepartamentoSeleccionado(value);
                  setMunicipioSeleccionado(null);
                }}
                disabled={!listDepartamento || listDepartamento.length === 0} // Asegura que listMunicipio es válido
                                showSearch
                optionFilterProp="children" // busca dentro del texto de las opciones
              >
                {listDepartamento.map((dep) => (
                  <Option key={dep} value={dep}>
                    {dep}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Filtros - Municipio*/}
            <Col xs={24} sm={8} md={5} lg={5}>
              <Select
                placeholder="Selecciona un Municipio"
                style={{ width: "100%" }}
                value={municipioSeleccionado || undefined} // Evita null
                onChange={(value) => setMunicipioSeleccionado(value)}
                disabled={!listMunicipio || listMunicipio.length === 0} // Asegura que listMunicipio es válido
                                showSearch
                optionFilterProp="children" // busca dentro del texto de las opciones
              >
                {listMunicipio?.map((mun) => (
                  <Option key={mun} value={mun}>
                    {mun}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Filtros - Tipo de novedad*/}
            <Col xs={24} sm={8} md={5} lg={5}>
              <Select
                placeholder="Selecciona un tipo de novedad"
                style={{ width: "100%" }}
                value={tipoNovedadSeleccionado || undefined} // Evita null
                onChange={(value) => setTipoNovedadSeleccionado(value)}
                disabled={!listTipoNovedad || listTipoNovedad.length === 0} // Asegura que listTipoNovedad es válido
                                showSearch
                optionFilterProp="children" // busca dentro del texto de las opciones
              >
                {listTipoNovedad?.map((nov) => (
                  <Option key={nov} value={nov}>
                    {nov}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Filtros - Rangos de fecha*/}
            <Col xs={24} sm={8} md={5} lg={6}>
              <RangePicker
                style={{ width: "100%" }}
                value={rangoFechas}
                onChange={(value) => {
                  setRangoFechas(value);
                }}
              />
            </Col>

            {/*Restablecer data */}
            <Col xs={6} sm={6} md={1} lg={1}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={() => {
                  setDepartamentoSeleccionado(null);
                  setMunicipioSeleccionado(null);
                  setTipoNovedadSeleccionado(null);
                  setNumeroVisitaSeleccionado(null);
                  setRangoFechas(null);
                }}
              ></Button>
            </Col>

            {/* Botón Exportar data */}
            <Col xs={18} sm={18} md={2} lg={2}>
              <ExportNovedades data={data}></ExportNovedades>
            </Col>
          </Row>

          {/*TOTALIZADORES EN CARDS */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={4}>
              <Card bordered={false} style={cardStyle}>
                <div
                  style={{ ...iconContainerStyle, backgroundColor: "#468CDE" }}
                >
                  <ManOutlined style={iconStyle} />
                </div>
                <h3
                  style={{
                    fontSize: 15,
                    fontWeight: "bold",
                    color: "#468CDE",
                    marginBottom: 5,
                  }}
                >
                  Total Novedades
                </h3>
                <h2 style={total}>{indicadores.totalNovedades}</h2>
              </Card>
            </Col>

            {/* total de novedades por estado de participante */}
            <Col xs={24} sm={24} md={12} lg={10}>
              <ChartCard
                title="Total Novedades por Estado del participante"
                data={{
                  labels: Object.keys(novedadesPorEstadoParticipante),
                  datasets: [
                    {
                      label: "Estado",
                      data: Object.values(novedadesPorEstadoParticipante),
                      backgroundColor: "#5B93D3",
                      borderRadius: 5, // Bordes redondeados en las barras
                      barThickness: 40, // Define el grosor de las barras
                    },
                  ],
                }}
                chartType="bar"
              />
            </Col>

            {/* %  de novedades por estado de participante */}
            <Col xs={24} sm={24} md={12} lg={10}>
              <ChartCard
                title="% de Novedades por Estado del Participante"
                chartType="pie"
                showAsPercentage={true}
                data={{
                  labels: Object.keys(novedadesPorEstadoParticipante),
                  datasets: [
                    {
                      data: Object.values(novedadesPorEstadoParticipante),
                      backgroundColor: [
                        "#145BAD",
                        "rgba(54, 162, 235, 0.6)",
                        "rgba(75, 192, 192, 0.6)",
                        "#D4D2D2",
                        "rgba(255, 99, 132, 0.6)",
                        "rgba(255, 159, 64, 0.6)",
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </Col>
          </Row>

          <Tag
            color="#D0E0F2"
            style={{
              color: "#145BAD",
              fontSize: "20px",
              fontWeight: "bold",
              padding: "8px 15px",
              borderRadius: "8px",
              textAlign: "left",
              display: "block",
              marginBottom: "15px",
              marginTop: "20px",
            }}
          >
            INFORMACIÓN GENERAL
          </Tag>

          <Row gutter={[16]} style={{ marginTop: 20 }}>
            {/* Gráfico 1: Novedades por Departamento o Municipio */}
            <Col xs={24} sm={24} md={12} lg={12}>
              <ChartCard
                title={
                  departamentoSeleccionado
                    ? `Número de Novedades por Municipio en ${departamentoSeleccionado}`
                    : "Número de Novedades por Departamento"
                }
                data={{
                  labels: departamentoSeleccionado
                    ? Object.keys(visitasPorMunicipio)
                    : Object.keys(visitasPorDepartamento),
                  datasets: [
                    {
                      label: municipioSeleccionado
                        ? "Municipio"
                        : departamentoSeleccionado
                        ? "Departamento"
                        : "Departamento",
                      data: departamentoSeleccionado
                        ? Object.values(visitasPorMunicipio)
                        : Object.values(visitasPorDepartamento),
                      backgroundColor: "#145BAD",
                      borderRadius: 5,
                      barThickness: 40,
                    },
                  ],
                }}
                backgroundColor="#145BAD"
              />
            </Col>

            {/*Gráfico 2 - % de novedades por departamento */}
            <Col xs={24} sm={24} md={12} lg={12}>
              <ChartCard
                title={
                  departamentoSeleccionado
                    ? `% de Novedades por Municipio en ${departamentoSeleccionado}`
                    : "% de Novedades por Departamento"
                }
                chartType="pie"
                showAsPercentage={true}
                data={{
                  labels: departamentoSeleccionado
                    ? Object.keys(visitasPorMunicipio)
                    : Object.keys(visitasPorDepartamento),
                  datasets: [
                    {
                      data: departamentoSeleccionado
                        ? Object.values(visitasPorMunicipio)
                        : Object.values(visitasPorDepartamento),
                      backgroundColor: [
                        "#145BAD",
                        "rgba(54, 162, 235, 0.6)",
                        "rgba(75, 192, 192, 0.6)",
                        "#D4D2D2",
                        "rgba(255, 99, 132, 0.6)",
                        "rgba(255, 159, 64, 0.6)",
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </Col>

            {/* total de novedades por tipo de novedad */}
            <Col xs={24} sm={24} md={12} lg={12}>
              <ChartCard
                title="Total Novedades por Tipo de Novedad"
                data={{
                  labels: Object.keys(novedadesPorTipoNovedad),
                  datasets: [
                    {
                      label: "Tipo de  Novedad",
                      data: Object.values(novedadesPorTipoNovedad),
                      backgroundColor: "#5B93D3",
                      borderRadius: 5, // Bordes redondeados en las barras
                      barThickness: 40, // Define el grosor de las barras
                    },
                  ],
                }}
                chartType="bar"
              />
            </Col>

            {/* % de novedades por tipo de novedad */}
            <Col xs={24} sm={24} md={12} lg={12}>
              <ChartCard
                title="% de Novedades por Tipo de Novedad"
                chartType="pie"
                showAsPercentage={true}
                data={{
                  labels: Object.keys(novedadesPorTipoNovedad),
                  datasets: [
                    {
                      data: Object.values(novedadesPorTipoNovedad),
                      backgroundColor: [
                        "#145BAD",
                        "rgba(54, 162, 235, 0.6)",
                        "rgba(75, 192, 192, 0.6)",
                        "#D4D2D2",
                        "rgba(255, 99, 132, 0.6)",
                        "rgba(255, 159, 64, 0.6)",
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </Col>

            {/* Gráfico 2: Numero de novedades por entidad responsable */}
            <Col xs={24} sm={24} md={12} lg={12}>
              <ChartCard
                title="Número de Novedades por Entidad Responsable"
                data={{
                  labels: Object.keys(novedadesPorResponsableEntidad),
                  datasets: [
                    {
                      label: "Entidad Responsable",
                      data: Object.values(novedadesPorResponsableEntidad),
                      backgroundColor: "#5B93D3",
                      borderRadius: 5,
                      barThickness: 40,
                    },
                  ],
                }}
                backgroundColor="#5B93D3"
              />
            </Col>

            {/* %  de novedades por entidad responsable*/}
            <Col xs={24} sm={24} md={12} lg={12}>
              <ChartCard
                title="% de Novedades por Entidad Responsable"
                chartType="pie"
                showAsPercentage={true}
                data={{
                  labels: Object.keys(novedadesPorResponsableEntidad),
                  datasets: [
                    {
                      data: Object.values(novedadesPorResponsableEntidad),
                      backgroundColor: [
                        "#145BAD",
                        "rgba(54, 162, 235, 0.6)",
                        "rgba(75, 192, 192, 0.6)",
                        "#D4D2D2",
                        "rgba(255, 99, 132, 0.6)",
                        "rgba(255, 159, 64, 0.6)",
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </Col>

            {/*Tabla de resumen datos generales */}
            <Col xs={24} sm={24} md={24} lg={24}>
              <div style={{ overflowX: "auto", marginTop: 30 }}>
                <Table
                  dataSource={dataFilter}
                  columns={columns}
                  rowKey="id"
                  scroll={{ y: 500, x: "max-content" }}
                  pagination={{
                    current: currentPage, // página actual
                    pageSize: pageSize, // filas por página
                    total: dataFilter.length, // total de registros
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} de ${total} registros`,
                    onChange: (page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    },
                  }}
                  bordered
                />
              </div>
            </Col>
          </Row>
        </Card>
      )}
    </>
  );
};

export default InformeNovedades;
